import {useMemo} from 'react';

import {
    buildArticleJsonLd,
    buildBreadcrumbsJsonLd,
    buildJobJsonLd,
    buildListJsonLd,
    buildOrganisationJsonLd
} from 'UI/modules/LachenschmidLayout/MetaData/utils';

import {isArticleType, isDefined, isDefinedAndPopulated, isJobPostingType} from 'Application/utils/typeGuards';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';

/**
 * Generates meta information and structured data used for SEO, social sharing, and canonical links.
 * This hook processes the provided `pageMetaData` object and returns normalized values for meta tags
 * such as canonical URL, page title, preview image, and structured data for JSON-LD output.
 * It is typically used in layout or `<Head>` components to populate Open Graph, Twitter Card,
 * and schema.org metadata.
 *
 * If the tenant is fully defined, it constructs a base URL for absolute links and combines it with
 * the page slug to form a complete canonical URL. It also generates `BreadcrumbList` and `LocalBusiness`
 * JSON-LD schemas based on tenant and folder data.
 *
 * @param meta             The full page metadata including SEO fields, tenant info, and optional breadcrumbs.
 * @param meta.seo         SEO-relevant data including title, slug, canonical URL, image, etc.
 * @param meta.tenant      Optional website context used to build full URLs and schema.org organization data.
 * @param meta.breadcrumbs Optional folder hierarchy used to build breadcrumb schema.
 * @returns An object containing normalized values: `canonicalUrl`, `image`, `structuredData`, `title`, and `url`.
 *
 * @example
 * ```tsx
 * const { canonicalUrl, title, image, structuredData } = useMetaData(pageMetaData);
 * ```
 */
// eslint-disable-next-line complexity
export const useMetaData = (meta?: PageData['pageMetaData']) => useMemo(() => {
    const {breadcrumbs, isHomepage, list, listData, listName} = meta?.structuredData ?? {};
    const {
        canonical: seoCanonical,
        description: seoDescription,
        image: seoImage,
        keywords: seoKeywords,
        slug: seoSlug,
        title: seoTitle
    } = meta?.seo ?? {};

    const baseUrl = isDefinedAndPopulated(meta?.tenant) ? `https://${meta.tenant.domain}` : '';
    const url = baseUrl && seoSlug ? `${baseUrl}${seoSlug}` : '';
    const canonicalUrl = seoCanonical ?? url;
    const title = seoTitle ?? 'Lachenschmid Kunststofftechnik';
    const image = isDefinedAndPopulated(seoImage) && isDefined(seoImage.url) ? `${baseUrl}${seoImage.sizes?.sm?.url ?? seoImage.url}` : undefined;
    const desc = seoDescription;
    const keywords = seoKeywords;
    const structuredData = [
        buildBreadcrumbsJsonLd(meta?.tenant, meta?.seo, breadcrumbs),
        listName ? buildListJsonLd(meta?.tenant, listName, list) : null,
        isArticleType(listData) ? buildArticleJsonLd(meta?.tenant, listData) : null,
        isJobPostingType(listData) ? buildJobJsonLd(meta?.tenant, listData) : null,
        isHomepage ? buildOrganisationJsonLd(meta?.tenant) : null
    ].filter(Boolean);

    return {
        canonicalUrl,
        desc,
        image,
        keywords,
        structuredData,
        title,
        url
    };
}, [meta]);